import type { Report } from '@reports/domain/entities';
import { createReportId } from '@reports/domain/entities';
import {
  ReportTypes,
  ReportInputTypes,
  ReportBlockTypes,
  ReportDataFormats,
  ReportChartTypes,
  ExportFormats,
} from '@reports/domain/types';
import {
  createTableBlockConfig,
  createChartBlockConfig,
  createReportFilterConfig,
  createExportConfig,
} from '@reports/domain/value-objects';

import { REPORT_ENDPOINTS } from '../endpoints';
import { REPORT_FILTERS_KEYS } from '../reportKeysConst';
import { REPORT_ACADEMY_LINKS } from '../reportAcademyConst';

export const cancellationReportDefinition: Report = {
  id: createReportId('cancellation-report'),
  type: ReportTypes.CANCELLATIONS,
  metadata: {
    name: 'reportCancellation:name',
    description: 'reportCancellation:description',
    tooltip: 'reportCancellation:tooltip',
    parrotAcademyLink: REPORT_ACADEMY_LINKS.CANCELLATION,
    filterDateRangeInfo: 'reportCancellation:filters.dateRangeInfo',
  },
  filters: [
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.PROVIDER,
      label: 'reportCommon:filterKeys.provider.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.provider.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.ORDER_TYPE,
      label: 'reportCommon:filterKeys.orderType.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.orderType.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.BRAND,
      label: 'reportCommon:filterKeys.brand.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.brand.placeholder',
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.USER_CANCELED,
      label: 'reportCommon:filterKeys.userCanceled.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.userCanceled.placeholder',
      searchable: true,
    }),
    createReportFilterConfig({
      key: REPORT_FILTERS_KEYS.USER_AUTHORIZED,
      label: 'reportCommon:filterKeys.userAuthorized.label',
      inputType: ReportInputTypes.MULTISELECT,
      placeholder: 'reportCommon:filterKeys.userAuthorized.placeholder',
      searchable: true,
    }),
  ],
  sections: [
    {
      key: 'charts',
      title: 'reportCommon:sections.charts.title',
      blocks: [
        createChartBlockConfig({
          key: 'accountCancellationsByDay',
          title: 'reportCancellation:blocks.charts.accountCancellationsByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/400`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportCancellation:blocks.charts.accountCancellationsByDay.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'accountCancellationsByReason',
          title: 'reportCancellation:blocks.charts.accountCancellationsByReason.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/402`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportCancellation:blocks.charts.accountCancellationsByReason.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'orderCancellationsByOrderType',
          title: 'reportCancellation:blocks.charts.orderCancellationsByOrderType.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/404`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportCancellation:blocks.charts.orderCancellationsByOrderType.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'itemCancellationsByDay',
          title: 'reportCancellation:blocks.charts.itemCancellationsByDay.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/401`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.BAR,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportCancellation:blocks.charts.itemCancellationsByDay.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: false,
            showTooltip: true,
            showTopBarLabels: false,
          },
        }),
        createChartBlockConfig({
          key: 'itemCancellationsByReason',
          title: 'reportCancellation:blocks.charts.itemCancellationsByReason.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/403`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportCancellation:blocks.charts.itemCancellationsByReason.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
        createChartBlockConfig({
          key: 'itemCancellationsByOrderType',
          title: 'reportCancellation:blocks.charts.itemCancellationsByOrderType.title',
          endpoint: `${REPORT_ENDPOINTS.CHARTS}/405`,
          type: ReportBlockTypes.CHART,
          chartType: ReportChartTypes.PIE,
          width: 'sm',
          seriesConfig: [
            {
              key: 'total_cancelled_amount',
              label: 'reportCancellation:blocks.charts.itemCancellationsByOrderType.series.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
            },
          ],
          options: {
            colors: ['#EB6668'],
            showLegend: true,
            showLabels: true,
            showTooltip: true,
            showTopBarLabels: false,
            groupTopValues: true,
          },
        }),
      ],
    },
    {
      key: 'summaryOrders',
      title: 'reportCancellation:sections.summaryOrders.title',
      blocks: [
        createTableBlockConfig({
          key: 'cancellationSummaryOrdersTable',
          endpoint: REPORT_ENDPOINTS.CANCELLATION.SUMMARY_ORDERS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'reason',
              label: 'reportCancellation:blocks.summaryTable.columns.reason.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'total_count',
              label: 'reportCancellation:blocks.summaryTable.columns.totalCount.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 0,
                },
              },
              align: 'right',
            },
            {
              key: 'total_amount',
              label: 'reportCancellation:blocks.summaryTable.columns.totalAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: false,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
    {
      key: 'orders',
      title: 'reportCancellation:sections.orders.title',
      blocks: [
        createTableBlockConfig({
          key: 'orderCancellationDetails',
          endpoint: REPORT_ENDPOINTS.CANCELLATION.ORDER_DETAILS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'cancellation_date',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.cancellationDate.label',
              format: {
                type: ReportDataFormats.DATE,
              },
              align: 'left',
            },
            {
              key: 'cancellation_time',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.cancellationTime.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'right',
            },
            {
              key: 'cancellation_amount',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.cancellationAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'order_reference',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.orderReference.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'order_type_name',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.orderTypeName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'cancellation_reason',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.cancellationReason.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'canceled_by_user',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.canceledByUser.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'authorized_by_user',
              label: 'reportCancellation:blocks.orderDetailsTable.columns.authorizedByUser.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
          ],
          columnToggleEnabled: true,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
    {
      key: 'summaryItems',
      title: 'reportCancellation:sections.summaryItems.title',
      blocks: [
        createTableBlockConfig({
          key: 'cancellationSummaryItemsTable',
          endpoint: REPORT_ENDPOINTS.CANCELLATION.SUMMARY_ITEMS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'reason',
              label: 'reportCancellation:blocks.summaryTable.columns.reason.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'total_count',
              label: 'reportCancellation:blocks.summaryTable.columns.totalCount.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 0,
                },
              },
              align: 'right',
            },
            {
              key: 'total_quantity',
              label: 'reportCancellation:blocks.summaryTable.columns.totalQuantity.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 2,
                },
              },
              align: 'right',
            },
            {
              key: 'total_amount',
              label: 'reportCancellation:blocks.summaryTable.columns.totalAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
          ],
          columnToggleEnabled: false,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
    {
      key: 'items',
      title: 'reportCancellation:sections.items.title',
      blocks: [
        createTableBlockConfig({
          key: 'itemsCancellationDetail',
          endpoint: REPORT_ENDPOINTS.CANCELLATION.ITEM_DETAILS,
          type: ReportBlockTypes.TABLE,
          columnsConfig: [
            {
              key: 'cancellation_date',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.cancellationDate.label',
              format: {
                type: ReportDataFormats.DATE,
              },
              align: 'left',
            },
            {
              key: 'cancellation_time',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.cancellationTime.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'right',
            },
            {
              key: 'cancellation_amount',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.cancellationAmount.label',
              format: {
                type: ReportDataFormats.CURRENCY,
                options: {
                  decimals: 2,
                  prefix: '$',
                },
              },
              align: 'right',
            },
            {
              key: 'order_reference',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.orderReference.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'cancellation_quantity',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.cancellationQuantity.label',
              format: {
                type: ReportDataFormats.NUMBER,
                options: {
                  decimals: 2,
                },
              },
              align: 'right',
            },
            {
              key: 'canceled_item_name',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.canceledItemName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'order_type_name',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.orderTypeName.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'cancellation_reason',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.cancellationReason.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'canceled_by_user',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.canceledByUser.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
            {
              key: 'authorized_by_user',
              label: 'reportCancellation:blocks.itemDetailsTable.columns.authorizedByUser.label',
              format: {
                type: ReportDataFormats.TEXT,
              },
              align: 'left',
            },
          ],
          columnToggleEnabled: true,
          paginationEnabled: true,
          pageSize: 50,
        }),
      ],
    },
  ],
  export: createExportConfig({
    name: 'reportCancellation:export.name',
    endpoint: REPORT_ENDPOINTS.CANCELLATION.EXPORT,
    slugs: ['406', '407', '409', '408'],
    format: ExportFormats.EXCEL,
  }),
};
